/**
 * Projective (homography) transform — Tier 1 Group E Phase 1b.
 *
 * Implements 4-corner-to-4-corner mapping used by Free Transform's
 * Distort and Perspective sub-modes. Canvas 2D's `setTransform`
 * supports affine (2×3 matrix) only — projective is one DOF more
 * (the perspective foreshortening), so we approximate via
 * tessellation: subdivide the source rect into a grid of cells,
 * map each cell's corners through the homography, and render each
 * cell as two affine triangles. At 16×16 subdivision the result is
 * visually indistinguishable from a true projective shader on
 * typical photo content; higher subdiv for very-tilted quads if
 * needed.
 *
 * Math:
 *   - The homography H is a 3×3 matrix that maps homogeneous
 *     [x, y, 1] in source space to [X', Y', W'] in destination
 *     space, with the final dst coord = [X'/W', Y'/W'].
 *   - With H[2,2] fixed at 1, there are 8 DOF — exactly the count
 *     needed to constrain by 4 point correspondences (each pair
 *     gives 2 equations).
 *   - DLT (Direct Linear Transform) sets up the 8x8 linear system
 *     and solves via Gauss-Jordan.
 *
 * Public surface:
 *   - `computeHomography(src, dst)` — returns the 9-element
 *     row-major matrix.
 *   - `applyHomography(H, x, y)` — point projection.
 *   - `rasterizeProjective(source, dstQuad, target, opts)` —
 *     render `source` onto `target` such that the source rect
 *     (0,0)-(w,h) maps to the user-supplied dst quad. Subdivision
 *     is configurable.
 */

export interface Point2 {
  x: number;
  y: number;
}

/** 9-element row-major matrix:
 *    [ h00 h01 h02 ]
 *    [ h10 h11 h12 ]
 *    [ h20 h21 h22 ]
 *  Tuple indexing is `[row * 3 + col]`. */
export type Homography = readonly [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
];

/** Source quad corners in scan order: top-left, top-right,
 *  bottom-right, bottom-left. Same order applies to `dst` — pairs
 *  match by index. */
export type Quad = readonly [Point2, Point2, Point2, Point2];

/** Compute the homography that maps `src[i]` → `dst[i]` for the
 *  4 point correspondences. Returns the identity matrix when the
 *  system is degenerate (collinear src points, etc.) — callers
 *  treat the identity result as "couldn't compute" and skip the
 *  transform.
 *
 *  The DLT setup:
 *    For each correspondence (xi, yi) → (Xi, Yi):
 *      Xi = (h00*xi + h01*yi + h02) / (h20*xi + h21*yi + 1)
 *      Yi = (h10*xi + h11*yi + h12) / (h20*xi + h21*yi + 1)
 *    Rearranged (multiplying through by the denominator):
 *      h00*xi + h01*yi + h02 - h20*xi*Xi - h21*yi*Xi = Xi
 *      h10*xi + h11*yi + h12 - h20*xi*Yi - h21*yi*Yi = Yi
 *    Stack 4 correspondences → 8 equations, 8 unknowns
 *    (h00..h21). Solve the 8x8 system. */
export function computeHomography(src: Quad, dst: Quad): Homography {
  // Build augmented matrix [A | b], 8 rows × 9 cols.
  const A: number[][] = [];
  for (let i = 0; i < 4; i++) {
    const { x: sx, y: sy } = src[i];
    const { x: dx, y: dy } = dst[i];
    A.push([sx, sy, 1, 0, 0, 0, -sx * dx, -sy * dx, dx]);
    A.push([0, 0, 0, sx, sy, 1, -sx * dy, -sy * dy, dy]);
  }
  // Gauss-Jordan with partial pivoting. 8 unknowns, augmented col 8.
  for (let col = 0; col < 8; col++) {
    let pivotRow = col;
    let pivotMag = Math.abs(A[col][col]);
    for (let r = col + 1; r < 8; r++) {
      const mag = Math.abs(A[r][col]);
      if (mag > pivotMag) {
        pivotMag = mag;
        pivotRow = r;
      }
    }
    if (pivotMag < 1e-12) {
      // Degenerate — return identity, caller will skip.
      return IDENTITY;
    }
    if (pivotRow !== col) {
      const tmp = A[col];
      A[col] = A[pivotRow];
      A[pivotRow] = tmp;
    }
    const pivot = A[col][col];
    for (let c = col; c < 9; c++) A[col][c] /= pivot;
    for (let r = 0; r < 8; r++) {
      if (r === col) continue;
      const factor = A[r][col];
      if (factor === 0) continue;
      for (let c = col; c < 9; c++) A[r][c] -= factor * A[col][c];
    }
  }
  // The augmented column now holds h00..h21 (h22 fixed at 1).
  return [A[0][8], A[1][8], A[2][8], A[3][8], A[4][8], A[5][8], A[6][8], A[7][8], 1] as const;
}

export const IDENTITY: Homography = [1, 0, 0, 0, 1, 0, 0, 0, 1] as const;

/** Project (x, y) through the homography. Returns the destination
 *  point after the perspective divide. */
export function applyHomography(H: Homography, x: number, y: number): Point2 {
  const w = H[6] * x + H[7] * y + H[8];
  if (w === 0) {
    return { x: 0, y: 0 };
  }
  return {
    x: (H[0] * x + H[1] * y + H[2]) / w,
    y: (H[3] * x + H[4] * y + H[5]) / w,
  };
}

/** Compute the affine matrix that maps source triangle (s0, s1, s2)
 *  to destination triangle (d0, d1, d2). Used by the tessellated
 *  rasterizer — within a single small triangle, the projective
 *  transform is approximately affine, so each triangle gets its
 *  own setTransform + drawImage with clipping to the dst triangle.
 *
 *  Returns the 6-element canvas2d transform tuple:
 *    [a, b, c, d, e, f] mapping src → screen via
 *      X = a*sx + c*sy + e
 *      Y = b*sx + d*sy + f
 *
 *  Returns null when the source triangle is degenerate (zero
 *  area). */
export function affineFromTriangle(
  s0: Point2,
  s1: Point2,
  s2: Point2,
  d0: Point2,
  d1: Point2,
  d2: Point2,
): [number, number, number, number, number, number] | null {
  // Solve for [a, b, c, d, e, f] such that:
  //   d0 = M * s0
  //   d1 = M * s1
  //   d2 = M * s2
  // Two 3x3 systems (one per dst axis) share the same source matrix.
  const sx0 = s0.x;
  const sy0 = s0.y;
  const sx1 = s1.x;
  const sy1 = s1.y;
  const sx2 = s2.x;
  const sy2 = s2.y;
  const det = sx0 * (sy1 - sy2) - sy0 * (sx1 - sx2) + (sx1 * sy2 - sx2 * sy1);
  if (Math.abs(det) < 1e-9) return null;
  const invDet = 1 / det;
  const dx0 = d0.x;
  const dx1 = d1.x;
  const dx2 = d2.x;
  const dy0 = d0.y;
  const dy1 = d1.y;
  const dy2 = d2.y;
  // a, c, e from x-coords; b, d, f from y-coords.
  const a = (dx0 * (sy1 - sy2) + dx1 * (sy2 - sy0) + dx2 * (sy0 - sy1)) * invDet;
  const c = (dx0 * (sx2 - sx1) + dx1 * (sx0 - sx2) + dx2 * (sx1 - sx0)) * invDet;
  const e =
    (dx0 * (sx1 * sy2 - sx2 * sy1) +
      dx1 * (sx2 * sy0 - sx0 * sy2) +
      dx2 * (sx0 * sy1 - sx1 * sy0)) *
    invDet;
  const b = (dy0 * (sy1 - sy2) + dy1 * (sy2 - sy0) + dy2 * (sy0 - sy1)) * invDet;
  const d = (dy0 * (sx2 - sx1) + dy1 * (sx0 - sx2) + dy2 * (sx1 - sx0)) * invDet;
  const f =
    (dy0 * (sx1 * sy2 - sx2 * sy1) +
      dy1 * (sx2 * sy0 - sx0 * sy2) +
      dy2 * (sx0 * sy1 - sx1 * sy0)) *
    invDet;
  return [a, b, c, d, e, f];
}

export interface RasterizeProjectiveOptions {
  /** Cells per axis (default 16 → 256 cells → 512 triangles). The
   *  visible artifact at 16 is sub-pixel for typical quads; 32 is
   *  necessary only for very-tilted perspectives. Cost scales as
   *  subdivisions². */
  subdivisions?: number;
}

/** Render `source` onto `target` such that the source's full rect
 *  (0,0) → (source.width, source.height) maps to the destination
 *  quad. The destination quad is in `target` pixel coords.
 *
 *  Implementation: subdivide the source rect into an N×N grid;
 *  each grid cell's 4 corners get projected through the
 *  homography; each cell is drawn as two affine triangles
 *  (upper-left and lower-right). The triangles are clipped via
 *  canvas2d `clip()` to prevent overflow at the seams. */
export function rasterizeProjective(
  source: HTMLCanvasElement | HTMLImageElement,
  dst: Quad,
  target: HTMLCanvasElement,
  opts: RasterizeProjectiveOptions = {},
): void {
  const ctx = target.getContext('2d');
  if (!ctx) return;
  const sw = source instanceof HTMLCanvasElement ? source.width : source.naturalWidth;
  const sh = source instanceof HTMLCanvasElement ? source.height : source.naturalHeight;
  if (sw <= 0 || sh <= 0) return;
  const subdivs = Math.max(1, Math.floor(opts.subdivisions ?? 16));

  const src: Quad = [
    { x: 0, y: 0 },
    { x: sw, y: 0 },
    { x: sw, y: sh },
    { x: 0, y: sh },
  ];
  const H = computeHomography(src, dst);

  ctx.save();
  for (let cy = 0; cy < subdivs; cy++) {
    for (let cx = 0; cx < subdivs; cx++) {
      // Source-space cell corners.
      const x0 = (cx * sw) / subdivs;
      const x1 = ((cx + 1) * sw) / subdivs;
      const y0 = (cy * sh) / subdivs;
      const y1 = ((cy + 1) * sh) / subdivs;
      const s00 = { x: x0, y: y0 };
      const s10 = { x: x1, y: y0 };
      const s11 = { x: x1, y: y1 };
      const s01 = { x: x0, y: y1 };
      // Destination-space corners via the homography.
      const d00 = applyHomography(H, s00.x, s00.y);
      const d10 = applyHomography(H, s10.x, s10.y);
      const d11 = applyHomography(H, s11.x, s11.y);
      const d01 = applyHomography(H, s01.x, s01.y);
      drawAffineTriangle(ctx, source, s00, s10, s11, d00, d10, d11);
      drawAffineTriangle(ctx, source, s00, s11, s01, d00, d11, d01);
    }
  }
  ctx.restore();
}

function drawAffineTriangle(
  ctx: CanvasRenderingContext2D,
  source: HTMLCanvasElement | HTMLImageElement,
  s0: Point2,
  s1: Point2,
  s2: Point2,
  d0: Point2,
  d1: Point2,
  d2: Point2,
): void {
  const M = affineFromTriangle(s0, s1, s2, d0, d1, d2);
  if (!M) return;
  ctx.save();
  // Clip path is the destination triangle expanded outward from
  // its centroid. Without this expansion, adjacent triangles
  // share a sub-pixel-precise edge whose anti-aliased coverage
  // doesn't sum to full opacity — the seams render as visible
  // grid + diagonal lines on the deformed result (and bake in
  // on Apply). With overdraw, neighboring triangles overlap;
  // since the affine maps are continuous across the edge (both
  // sides paint the same source pixel at the same destination),
  // the overlap is invisible and the seams disappear.
  //
  // Expand magnitude target is 1.5 dest pixels — that renders
  // as ~3.75 screen pixels at typical preview downscale, enough
  // to fully cover canvas2d's 1–2 screen-pixel AA gradient.
  // BUT for small dst quads (e.g. a 50×50-bbox layer in a
  // 1920-wide scene preview-scaled to 0.4× produces ~20px-wide
  // dst quads → ~1.7×0.8px triangles), a fixed 1.5px expand is
  // larger than the triangle itself and inverts the clip path
  // — no pixels get drawn. Cap the expand at a fraction of the
  // triangle's shortest edge so degenerate-but-valid triangles
  // still get some overdraw without inversion.
  const e01 = Math.hypot(d1.x - d0.x, d1.y - d0.y);
  const e12 = Math.hypot(d2.x - d1.x, d2.y - d1.y);
  const e20 = Math.hypot(d0.x - d2.x, d0.y - d2.y);
  const minEdge = Math.min(e01, e12, e20);
  const seamOverlapPx = Math.min(1.5, Math.max(0.25, minEdge * 0.25));
  const cx = (d0.x + d1.x + d2.x) / 3;
  const cy = (d0.y + d1.y + d2.y) / 3;
  const expand = (p: Point2): Point2 => {
    const dx = p.x - cx;
    const dy = p.y - cy;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) return p;
    return {
      x: p.x + (dx / len) * seamOverlapPx,
      y: p.y + (dy / len) * seamOverlapPx,
    };
  };
  const e0 = expand(d0);
  const e1 = expand(d1);
  const e2 = expand(d2);
  ctx.beginPath();
  ctx.moveTo(e0.x, e0.y);
  ctx.lineTo(e1.x, e1.y);
  ctx.lineTo(e2.x, e2.y);
  ctx.closePath();
  ctx.clip();
  ctx.transform(M[0], M[1], M[2], M[3], M[4], M[5]);
  ctx.drawImage(source, 0, 0);
  ctx.restore();
}
